import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { transactionService } from '../../services/transactionService';
import { subscriptionService } from '../../services/subscriptionService';
import { KashierTransaction } from '../../types/payment';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import { CheckCircle, Download, ArrowLeft, Printer, Home } from 'lucide-react';
import { Loader2 } from 'lucide-react';

const BillPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const { user, isLoggedIn, loading: authLoading } = useAuth();
  const { language, isRTL } = useLocalization();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<KashierTransaction | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🔍 BillPage useEffect:', { transactionId, user, isLoggedIn, authLoading });
    
    if (authLoading) {
      // Still loading authentication
      return;
    }
    
    if (!isLoggedIn || !user) {
      console.log('❌ User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    
    if (!transactionId) {
      console.log('❌ No transaction ID, redirecting to dashboard');
      navigate('/dashboard');
      return;
    }
    
    console.log('✅ Loading transaction:', transactionId);
    loadTransaction();
  }, [transactionId, user, isLoggedIn, authLoading, navigate]);

  const loadTransaction = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!transactionId) {
        throw new Error('Transaction ID not provided');
      }

      // Load transaction from Firestore
      const transactionData = await transactionService.getTransactionById(transactionId);
      
      if (!transactionData) {
        throw new Error('Transaction not found');
      }

      setTransaction(transactionData);
    } catch (error) {
      console.error('Error loading transaction:', error);
      setError(error instanceof Error ? error.message : 'Failed to load transaction');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(parseFloat(amount));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // TODO: Implement PDF generation
    alert(language === 'ar' ? 'جاري تنزيل الفاتورة...' : 'Downloading invoice...');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoBack = () => {
    navigate('/subscription');
  };

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
        <AnnouncementBar />
        
        {/* Header */}
        <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MorafaaLogo className="h-8 w-auto" />
                <h1 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'مرافعة' : 'Morafaa'}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {authLoading 
                ? (language === 'ar' ? 'جاري التحقق من الهوية...' : 'Verifying authentication...')
                : (language === 'ar' ? 'جاري تحميل الفاتورة...' : 'Loading invoice...')
              }
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {language === 'ar' ? 'يرجى الانتظار...' : 'Please wait...'}
            </p>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
        <AnnouncementBar />
        
        {/* Header */}
        <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MorafaaLogo className="h-8 w-auto" />
                <h1 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'مرافعة' : 'Morafaa'}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Error Content */}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 dark:text-red-400 text-2xl">!</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'ar' ? 'خطأ' : 'Error'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {error || (language === 'ar' ? 'لم يتم العثور على الفاتورة' : 'Invoice not found')}
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={handleGoBack}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {language === 'ar' ? 'العودة' : 'Go Back'}
              </button>
              <button
                onClick={handleGoToDashboard}
                className="bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-colors"
              >
                {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      
      {/* Header */}
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MorafaaLogo className="h-8 w-auto" />
              <h1 className="text-xl font-bold text-white">
                {language === 'ar' ? 'مرافعة' : 'Morafaa'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <h2 className="text-lg font-semibold text-white">
                {language === 'ar' ? 'فاتورة الدفع' : 'Payment Invoice'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handleGoBack}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{language === 'ar' ? 'العودة' : 'Back'}</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                <Printer className="w-4 h-4" />
                <span>{language === 'ar' ? 'طباعة' : 'Print'}</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>{language === 'ar' ? 'تحميل PDF' : 'Download PDF'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-8 print:shadow-none print:border-0">
          {/* Invoice Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'ar' ? 'فاتورة الدفع' : 'Payment Invoice'}
            </h2>
            <p className="text-green-600 font-semibold">
              {language === 'ar' ? 'تم الدفع بنجاح' : 'Payment Successful'}
            </p>
          </div>

          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800">
                  {language === 'ar' ? 'تم تأكيد الدفع' : 'Payment Confirmed'}
                </h3>
                <p className="text-green-700 text-sm">
                  {language === 'ar' 
                    ? 'تم دفع المبلغ بنجاح وتم تفعيل اشتراكك'
                    : 'Your payment has been processed and your subscription is now active'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Transaction Info */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4 text-lg">
                {language === 'ar' ? 'تفاصيل المعاملة' : 'Transaction Details'}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'رقم المعاملة:' : 'Transaction ID:'}
                  </span>
                  <span className="font-mono text-sm">{transaction.transactionId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'رقم الطلب:' : 'Order ID:'}
                  </span>
                  <span className="font-mono text-sm">{transaction.orderId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'المبلغ:' : 'Amount:'}
                  </span>
                  <span className="font-semibold text-lg text-green-600">
                    {formatCurrency(transaction.transactionAmount, transaction.transactionCurrency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'حالة الدفع:' : 'Payment Status:'}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {transaction.paymentStatus}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'تاريخ الدفع:' : 'Payment Date:'}
                  </span>
                  <span>{formatDate(transaction.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4 text-lg">
                {language === 'ar' ? 'تفاصيل الاشتراك' : 'Subscription Details'}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'اسم الخطة:' : 'Plan Name:'}
                  </span>
                  <span className="font-semibold">{transaction.subscriptionPlanName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'مدة الاشتراك:' : 'Duration:'}
                  </span>
                  <span>{transaction.subscriptionPlanDuration} {transaction.subscriptionPlanDurationType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'السعر:' : 'Price:'}
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(transaction.subscriptionPlanPrice, transaction.transactionCurrency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {language === 'ar' ? 'الوصف:' : 'Description:'}
                  </span>
                  <span className="text-sm text-gray-700">{transaction.subscriptionPlanDescription}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="border-t pt-8">
            <h4 className="font-semibold text-gray-900 mb-4 text-lg">
              {language === 'ar' ? 'تفاصيل العميل' : 'Customer Details'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-600">
                  {language === 'ar' ? 'الاسم:' : 'Name:'}
                </span>
                <p className="font-semibold">{transaction.userName}</p>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}
                </span>
                <p className="font-semibold">{transaction.userEmail}</p>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'ar' ? 'الهاتف:' : 'Phone:'}
                </span>
                <p className="font-semibold">{transaction.userPhone}</p>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'ar' ? 'تاريخ الدفع:' : 'Payment Date:'}
                </span>
                <p className="font-semibold">{formatDate(transaction.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="border-t pt-8 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-600">
                  {language === 'ar' 
                    ? 'شكراً لاستخدامك منصة مرافعة'
                    : 'Thank you for using Morafaa platform'
                  }
                </p>
                <p className="text-xs text-gray-500">
                  {language === 'ar' 
                    ? 'هذه الفاتورة صالحة كإثبات دفع'
                    : 'This invoice is valid as proof of payment'
                  }
                </p>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={handleGoBack}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  {language === 'ar' ? 'العودة' : 'Go Back'}
                </button>
                <button
                  onClick={handleGoToDashboard}
                  className="flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded-lg font-semibold hover:bg-accent-dark transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span>{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BillPage;
